// Centralized caching layer for the app
const CACHE_PREFIX = 'app_cache_'

interface CacheEntry<T> {
  data: T
  expires: number
}

export function setCache<T>(key: string, data: T, ttlMinutes: number = 30): void {
  const entry: CacheEntry<T> = {
    data,
    expires: Date.now() + ttlMinutes * 60 * 1000,
  }
  try {
    const serialized = JSON.stringify(entry)
    // Guard against localStorage quota (max ~5MB per origin)
    if (serialized.length > 2 * 1024 * 1024) {
      console.warn(`Cache entry '${key}' too large (${(serialized.length / 1024).toFixed(0)}KB), skipping`)
      return
    }
    localStorage.setItem(CACHE_PREFIX + key, serialized)
  } catch {
    // Storage full — try to clear expired entries first, then retry
    try {
      clearExpiredCache()
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry))
    } catch {
      // Still full — silently fail
    }
  }
}

export function getCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key)
    if (!raw) return null
    const entry: CacheEntry<T> = JSON.parse(raw)
    if (Date.now() > entry.expires) {
      localStorage.removeItem(CACHE_PREFIX + key)
      return null
    }
    return entry.data
  } catch {
    return null
  }
}

export function clearCache(key: string): void {
  localStorage.removeItem(CACHE_PREFIX + key)
}

export function clearAllCache(): void {
  const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX))
  keys.forEach(k => localStorage.removeItem(k))
}

export function clearExpiredCache(): void {
  const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX))
  keys.forEach(k => {
    try {
      const raw = localStorage.getItem(k)
      if (!raw) return
      const entry = JSON.parse(raw)
      if (entry.expires && Date.now() > entry.expires) {
        localStorage.removeItem(k)
      }
    } catch {
      localStorage.removeItem(k) // Corrupt entry — remove it
    }
  })
}

// Specific cache keys
export const CACHE_KEYS = {
  SUBSCRIPTION: 'subscription_status',
  PROFILE: (userId: string) => `profile_${userId}`,
  LESSONS: (category: string) => `lessons_${category}`,
  QUIZ_PROGRESS: (userId: string, category: string) => `quiz_${userId}_${category}`,
} as const
